import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import studentMasterApi from "./studentMasterApi";
import PropTypes from "prop-types";
const SCOPE_COLUMNS = ["admittedbatch", "program_name", "branch_name"];

const STUDENT_COLUMNS = [
  "student_id",
  "college_id",
  "user_id",
  "admission_no",
  "scholar_app_no",
  "admission_date",
  "student_rollno",
  "aadhaar_no",
  "student_name",
  "father_name",
  "mother_name",
  "dob",
  "gender",
  "email",
  "mobile",
  "photo_url",
  "address",
  "admission_type",
  "student_status",
  "second_language",
  "seat_type",
  "section",
  "nationality",
  "religion",
  "caste",
  "subcaste",
  "student_first_name",
  "student_middle_name",
  "student_sur_last_name",
  "father_aadhar",
  "mother_aadhar",
  "father_mobile",
  "mother_mobile",
  "father_occupation",
  "mother_occupation",
  "mole_1",
  "mole_2",
  "aapara_id",
  "oamdc_no",
  "caste_cert_no",
  "income_cert_no",
  "ews_cert_no",
  "ration_card_no",
  "guardian_name",
  "guardian_mobile",
  "guardian_relation",
  "door_no",
  "street",
  "village",
  "mandal",
  "district",
  "pincode",
  "state",
  "ssc_hall_ticket_no",
  "ssc_cgpa",
  "ssc_school_name",
  "school_city",
  "ssc_year",
  "inter_hall_ticket_no",
  "inter_cgpa",
  "inter_college_name",
  "inter_city",
  "inter_year",
  "diploma_hall_ticket_no",
  "diploma_cgpa",
  "diploma_college_name",
  "diploma_city",
  "diploma_year",
  "degree_hall_ticket_no",
  "degree_cgpa",
  "degree_college_name",
  "degree_city",
  "degree_year",
  "pg_hall_ticket_no",
  "pg_cgpa",
  "pg_college_name",
  "pg_city",
  "pg_year",
  "college_left_date",
  "ssc_tc_no",
  "ssc_tc_date",
  "inter_tc_no",
  "inter_tc_date",
  "diploma_tc_no",
  "diploma_tc_date",
  "degree_tc_no",
  "degree_tc_date",
  "cet_hall_ticket_no",
  "cet_rank",
  "blood_group",
  "phc_status",
  "certificate_status",
];

const ALL_COLUMNS = [...SCOPE_COLUMNS, ...STUDENT_COLUMNS];

const DATE_COLUMNS = [
  "admission_date",
  "dob",
  "college_left_date",
  "ssc_tc_date",
  "inter_tc_date",
  "diploma_tc_date",
  "degree_tc_date",
];
const txt = (v) => (v == null ? "" : String(v).trim());
const normalized = (v) => txt(v).replace(/\s+/g, " ").toLocaleLowerCase();

function excelDateToISO_old(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const s = txt(value);
  if (!s) return "";

  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
    const [year, month, day] = s.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    ) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }

    return s;
  }

  const match = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);

  if (match) {
    const [, dayText, monthText, yearText] = match;
    const day = Number(dayText);
    const month = Number(monthText);
    const year = Number(yearText);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    ) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }

    return s;
  }

  if (/^\d+(\.\d+)?$/.test(s)) {
    const date = XLSX.SSF.parse_date_code(Number(s));

    if (date) {
      return `${String(date.y).padStart(4, "0")}-${String(date.m).padStart(2, "0")}-${String(
        date.d
      ).padStart(2, "0")}`;
    }
  }

  return s;
}
function excelDateToISO(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = value.getDate();

    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  const s = txt(value);
  if (!s) return "";

  // YYYY-MM-DD or YYYY-M-D
  let match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

  if (match) {
    const [, yearText, monthText, dayText] = match;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);

    return validISODate(year, month, day) || s;
  }

  // DD-MM-YYYY or DD/MM/YYYY
  match = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);

  if (match) {
    const [, dayText, monthText, yearText] = match;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);

    return validISODate(year, month, day) || s;
  }

  // Excel numeric date serial
  if (/^\d+(\.\d+)?$/.test(s)) {
    const date = XLSX.SSF.parse_date_code(Number(s));

    if (date) {
      const year = Number(date.y);
      const month = Number(date.m);
      const day = Number(date.d);

      return validISODate(year, month, day) || s;
    }
  }

  return s;
}

function validISODate(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return "";
  }

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
export function validateStudentImportRow(row) {
  const errors = [];

  const digitCheck = (key, label, length) => {
    const value = txt(row[key]);
    if (value && !new RegExp(`^\\d{${length}}$`).test(value)) {
      errors.push(`${label} must contain exactly ${length} digits.`);
    }
  };

  if (!txt(row.student_first_name)) {
    errors.push("Student first name is required.");
  }

  if (!txt(row.student_sur_last_name)) {
    errors.push("Student surname is required.");
  }

  if (!txt(row.admittedbatch)) errors.push("Admitted batch is missing.");
  if (!txt(row.program_name)) errors.push("Program name is missing.");
  if (!txt(row.branch_name)) errors.push("Branch name is missing.");

  digitCheck("aadhaar_no", "Aadhaar number", 12);
  digitCheck("mobile", "Mobile number", 10);
  digitCheck("father_mobile", "Father mobile", 10);
  digitCheck("mother_mobile", "Mother mobile", 10);
  digitCheck("guardian_mobile", "Guardian mobile", 10);
  digitCheck("father_aadhar", "Father Aadhaar", 12);
  digitCheck("mother_aadhar", "Mother Aadhaar", 12);
  digitCheck("pincode", "Pincode", 6);

  const email = txt(row.email);
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.push("Email address is invalid.");
  }

  for (const field of DATE_COLUMNS) {
    const value = txt(row[field]);
    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      errors.push(`${field} must be a valid date in YYYY-MM-DD format.`);
    }
  }

  const seatType = txt(row.seat_type).toUpperCase();
  if (seatType && !["C", "M"].includes(seatType)) {
    errors.push("Seat type must be C or M.");
  }

  const admissionType = txt(row.admission_type).toUpperCase();
  if (admissionType && !["REGULAR", "LATERAL", "TRANSFER", "REJOIN"].includes(admissionType)) {
    errors.push("Invalid admission type.");
  }

  const gender = txt(row.gender).toUpperCase();
  if (gender && !["M", "F", "O"].includes(gender)) {
    errors.push("Gender must be M, F or O.");
  }

  return errors;
}
export function normalizeImportDate(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  let year, month, day;

  // Excel date cell returned as JavaScript Date
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";

    year = value.getFullYear();
    month = value.getMonth() + 1;
    day = value.getDate();
  }
  // Excel numeric date serial
  else if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(Date.UTC(1899, 11, 30) + Math.floor(value) * 86400000);

    if (Number.isNaN(date.getTime())) return "";

    year = date.getUTCFullYear();
    month = date.getUTCMonth() + 1;
    day = date.getUTCDate();
  } else {
    const input = String(value).trim();

    // YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
    let match = input.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|T)/);

    if (match) {
      [, year, month, day] = match;
    } else {
      // DD-MM-YYYY
      match = input.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);

      if (!match) return "";

      [, day, month, year] = match;
    }
  }

  year = Number(year);
  month = Number(month);
  day = Number(day);

  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return "";
  }

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function makeTemplate(selection) {
  const row = {
    admittedbatch: selection?.batchName || "",
    program_name: selection?.programName || "",
    branch_name: selection?.branchName || "",
  };
  ALL_COLUMNS.forEach((key) => {
    if (!(key in row)) row[key] = "";
  });
  const sheet = XLSX.utils.json_to_sheet([row], { header: ALL_COLUMNS });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Students");
  const branch = (selection?.branchName || "branchName")
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, "_");
  XLSX.writeFile(workbook, `StudentMaster_${branch}.xlsx`);
}

function makeCsvTemplate(selection) {
  const values = ALL_COLUMNS.map((key) => {
    if (key === "admittedbatch") return selection?.batchName || "";
    if (key === "program_name") return selection?.programName || "";
    if (key === "branch_name") return selection?.branchName || "";
    return "";
  });
  const quote = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const csv = `${ALL_COLUMNS.map(quote).join(",")}\r\n${values.map(quote).join(",")}\r\n`;
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const branch = (selection?.branchName || "branchName")
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, "_");
  link.href = url;
  link.download = `StudentMaster_${branch}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function UploadStudentsDialog({ open, onClose, selection, onUploaded }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const inputRef = useRef(null);
  useEffect(() => {
    if (!open) {
      setFile(null);
      setRows([]);
      setError("");
      setLoadingPreview(false);
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [open]);

  const preview = async () => {
    if (!file) {
      setError("Choose an Excel (.xlsx/.xls) or CSV file first.");
      return;
    }
    if (!selection?.batchId || !selection?.programId || !selection?.branchId) {
      setError("Select a valid batch, program and branch first.");
      return;
    }
    setLoadingPreview(true);
    setError("");
    setRows([]);
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array", cellDates: true });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        defval: "",
        raw: true,
        blankrows: false,
      });

      if (!matrix.length) {
        throw new Error("The selected file contains no student rows.");
      }

      const normalizeHeader = (value) =>
        String(value ?? "")
          .replace(/^\uFEFF/, "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "_");

      // Find the row containing the greatest number of expected columns.
      let headerRowIndex = -1;
      let bestMatchCount = 0;

      matrix.forEach((row, index) => {
        const headers = row.map(normalizeHeader);
        const matchCount = ALL_COLUMNS.filter((column) => headers.includes(column)).length;

        if (matchCount > bestMatchCount) {
          bestMatchCount = matchCount;
          headerRowIndex = index;
        }
      });

      if (headerRowIndex === -1 || bestMatchCount < 5) {
        const preview = matrix
          .slice(0, 5)
          .map((row, index) => `Row ${index + 1}: ${row.join(" | ")}`)
          .join("\n");

        throw new Error(`Unable to identify the student template header row.\n${preview}`);
      }

      const headers = matrix[headerRowIndex].map(normalizeHeader);

      const missing = ALL_COLUMNS.filter((column) => !headers.includes(column));

      if (missing.length) {
        throw new Error(`Missing template columns: ${missing.join(", ")}`);
      }

      // Convert worksheet rows into objects using the detected header row.
      const source = matrix
        .slice(headerRowIndex + 1)
        .filter((row) => row.some((value) => txt(value) !== ""))
        .map((row) => {
          const obj = {};

          headers.forEach((header, index) => {
            if (header) {
              obj[header] = row[index] ?? "";
            }
          });

          return obj;
        });

      if (!source.length) {
        throw new Error("The selected file contains no student rows.");
      }
      const localRows = source.map((sourceRow, i) => {
        const row = {};

        Object.entries(sourceRow).forEach(([key, value]) => {
          row[key.trim().toLowerCase()] = value;
        });

        DATE_COLUMNS.forEach((field) => {
          row[field] = excelDateToISO(row[field]);
        });

        row.seat_type = txt(row.seat_type).toUpperCase();
        row.admission_type = txt(row.admission_type).toUpperCase();
        row.gender = txt(row.gender).toUpperCase();
        row.student_status = txt(row.student_status).toUpperCase();

        const errors = validateStudentImportRow(row);

        if (normalized(row.admittedbatch) !== normalized(selection.batchName)) {
          errors.push(
            `Batch mismatch: file has "${txt(row.admittedbatch)}", selected "${
              selection.batchName
            }".`
          );
        }

        if (normalized(row.program_name) !== normalized(selection.programName)) {
          errors.push(
            `Program mismatch: file has "${txt(row.program_name)}", selected "${
              selection.programName
            }".`
          );
        }

        if (normalized(row.branch_name) !== normalized(selection.branchName)) {
          errors.push(
            `Branch mismatch: file has "${txt(row.branch_name)}", selected "${
              selection.branchName
            }".`
          );
        }

        return {
          ...row,
          id: i + 1,
          excel_row: i + 2,
          errors,
        };
      });
      const studentIds = new Map();

      localRows.forEach((row) => {
        const studentId = txt(row.student_id);

        if (!studentId) return;

        if (studentIds.has(studentId)) {
          row.errors.push(`Duplicate student_id in file (also row ${studentIds.get(studentId)}).`);

          const other = localRows.find((item) => item.excel_row === studentIds.get(studentId));

          if (other) {
            other.errors.push(`Duplicate student_id in file (also row ${row.excel_row}).`);
          }
        } else {
          studentIds.set(studentId, row.excel_row);
        }
      });
      const serverCheck = await studentMasterApi.validateStudentImport(
        selection,
        localRows.map(({ id, excel_row, errors, ...row }) => row)
      );
      const serverRows = serverCheck?.data || serverCheck?.rows || [];
      if (serverRows.length === localRows.length) {
        localRows.forEach((row, i) => {
          row.errors = [...new Set([...row.errors, ...(serverRows[i]?.errors || [])])];
        });
      }
      setRows(localRows);
      setPage(0);
      if (localRows.some((r) => r.errors.length))
        setError(
          "Some rows have validation errors. Correct the Excel/CSV file and upload it again."
        );
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Unable to preview the file.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const confirmUpload = async () => {
    if (!rows.length || rows.some((r) => r.errors?.length)) return;
    setUploading(true);
    setError("");
    try {
      const payloadRows = rows.map(({ id, excel_row, errors, ...row }) => row);
      const result = await studentMasterApi.importStudents(selection, payloadRows);
      setRows([]);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onUploaded?.(result);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Student upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "excel_row", headerName: "Excel Row", width: 90 },
      { field: "student_rollno", headerName: "Roll No. (optional)", width: 170 },
      { field: "student_first_name", headerName: "First Name", width: 150 },
      { field: "student_middle_name", headerName: "Middle Name", width: 150 },
      { field: "student_sur_last_name", headerName: "Surname", width: 150 },
      { field: "aadhaar_no", headerName: "Aadhaar", width: 140 },
      { field: "email", headerName: "Email", width: 190 },
      { field: "mobile", headerName: "Mobile", width: 130 },
      { field: "dob", headerName: "DOB", width: 120 },
      { field: "seat_type", headerName: "Seat", width: 85 },
      { field: "admission_type", headerName: "Admission Type", width: 140 },
      {
        field: "errors",
        headerName: "Validation Errors",
        minWidth: 340,
        flex: 1,
        renderCell: (p) => (
          <Typography
            color={p.value?.length ? "error" : "success.main"}
            variant="caption"
            sx={{ whiteSpace: "normal", py: 1 }}
          >
            {(p.value || []).join("; ") || "Valid"}
          </Typography>
        ),
      },
    ],
    []
  );
  const invalidCount = rows.filter((r) => r.errors?.length).length;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown" && !uploading) onClose();
      }}
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle sx={{ pr: 7 }}>
        Upload Students
        <Stack direction="row" spacing={1} sx={{ position: "absolute", right: 56, top: 12 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => makeTemplate(selection)}
            sx={{
              color: "#1976d2",
              borderColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#e3f2fd",
                borderColor: "#1565c0",
              },
            }}
          >
            Excel Template
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => makeCsvTemplate(selection)}
            sx={{
              color: "#2e7d32",
              borderColor: "#2e7d32",
              "&:hover": {
                backgroundColor: "#e8f5e9",
                borderColor: "#1b5e20",
              },
            }}
          >
            CSV Template
          </Button>
        </Stack>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={uploading}
          sx={{ position: "absolute", right: 8, top: 8, color: "error.main" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Template metadata (batch, program and branch) is checked against this selected row. Keep
          all three metadata values on every student row. Format Aadhaar and mobile columns as Text.
          Blank student_rollno values are generated by the server.
        </Alert>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => inputRef.current?.click()}
            sx={{
              color: "#1976d2",
              borderColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#e3f2fd",
                borderColor: "#1565c0",
              },
            }}
          >
            Choose Excel / CSV
          </Button>
          <input
            ref={inputRef}
            type="file"
            hidden
            accept=".xlsx,.xls,.csv"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setRows([]);
              setError("");
            }}
          />
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {file?.name || "No file selected"}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={preview}
            disabled={!file || loadingPreview || uploading}
            sx={{
              backgroundColor: "#7b1fa2",
              color: "#fff",
              "&:hover": { backgroundColor: "#6a1b9a" },
            }}
          >
            {loadingPreview ? <CircularProgress size={20} color="inherit" /> : "Preview & Validate"}
          </Button>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!!rows.length && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Alert severity={invalidCount ? "error" : "success"} sx={{ py: 0 }}>
                {rows.length} rows · {rows.length - invalidCount} valid · {invalidCount} invalid
              </Alert>
              {invalidCount > 0 && (
                <Typography variant="caption" color="error" sx={{ alignSelf: "center" }}>
                  Confirm Upload is disabled until all errors are corrected.
                </Typography>
              )}
            </Stack>
            <Box sx={{ height: 420, width: "100%" }}>
              <Box sx={{ height: 420, width: "100%" }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  getRowHeight={() => 72}
                  page={page}
                  onPageChange={(newPage) => setPage(newPage)}
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  disableSelectionOnClick
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#e3f2fd",
                      color: "#0d47a1",
                      fontWeight: 700,
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: "#fafafa",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#eaf4ff",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={uploading}
          sx={{
            color: "#d32f2f",
            "&:hover": { backgroundColor: "#ffebee" },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={confirmUpload}
          disabled={!rows.length || invalidCount > 0 || uploading}
          sx={{
            backgroundColor: "#2e7d32",
            color: "#fff",
            "&:hover": { backgroundColor: "#1b5e20" },
            "&.Mui-disabled": {
              backgroundColor: "#c8e6c9",
              color: "#fff",
            },
          }}
        >
          {uploading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            `Confirm Upload (${rows.length})`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function UploadPhotosDialog({ open, onClose, selection, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  useEffect(() => {
    if (!open) {
      setFiles([]);
      setError("");
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [open]);
  const submit = async () => {
    if (!files.length) {
      setError("Choose student photo files first.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const result = await studentMasterApi.uploadStudentPhotos(selection, files);
      onUploaded?.(result);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Photo upload failed.");
    } finally {
      setUploading(false);
    }
  };
  return (
    <Dialog open={open} onClose={() => !uploading && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>
        Upload Student Photos{" "}
        <IconButton
          onClick={onClose}
          disabled={uploading}
          sx={{ position: "absolute", right: 8, top: 8, color: "error.main" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Select passport-size PNG files named exactly as the student roll number, for example{" "}
          <b>IC_2026_0001.png</b>. Each file must be 70–100 KB and portrait passport-photo
          proportions (at least 200 × 250 pixels). The server verifies the image signature,
          dimensions, and that the roll number belongs to this selected college, batch, program and
          branch.
        </Alert>
        <Button variant="outlined" onClick={() => inputRef.current?.click()}>
          Choose Photos
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".png,image/png"
          multiple
          hidden
          onChange={(e) => {
            const selectedFiles = Array.from(e.target.files || []);
            const minBytes = 20 * 1024;
            const maxBytes = 1000 * 1024;
            const invalid = selectedFiles.filter(
              (file) =>
                !file.name.endsWith(".png") ||
                file.type !== "image/png" ||
                file.size < minBytes ||
                file.size > maxBytes
            );
            if (invalid.length) {
              setFiles([]);
              setError(
                `Only PNG files between 20 KB and 100 KB are allowed. Invalid: ${invalid
                  .map((file) => file.name)
                  .join(", ")}`
              );
            } else {
              setFiles((previous) => [...previous, ...selectedFiles]);
              setError("");
            }

            // Allows selecting the same file again after correcting an error.
            e.target.value = "";
          }}
        />
        <Typography variant="body2" sx={{ mt: 1 }}>
          {files.length
            ? `${files.length} photo(s) selected: ${files.map((f) => f.name).join(", ")}`
            : "No photos selected"}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={uploading}
          sx={{
            color: "#d32f2f",
            "&:hover": { backgroundColor: "#ffebee" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!files.length || uploading}
          sx={{
            backgroundColor: "#2e7d32",
            color: "#fff",
            "&:hover": { backgroundColor: "#1b5e20" },
            "&.Mui-disabled": {
              backgroundColor: "#c8e6c9",
              color: "#fff",
            },
          }}
        >
          {uploading ? <CircularProgress size={20} color="inherit" /> : "Upload Photos"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
UploadStudentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selection: PropTypes.shape({
    batchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    programId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    branchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    batchName: PropTypes.string,
    programName: PropTypes.string,
    branchName: PropTypes.string,
  }).isRequired,
  onUploaded: PropTypes.func,
};

UploadPhotosDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selection: PropTypes.shape({
    batchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    programId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    branchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    batchName: PropTypes.string,
    programName: PropTypes.string,
    branchName: PropTypes.string,
  }).isRequired,
  onUploaded: PropTypes.func,
};
